import _ from 'lodash';
import {
    BaseCells,
    BaseTextInfo,
    BlendModeType,
    Border,
    BrushType,
    CellStyle,
    ChartSeriesItemStyle,
    ChartTextStyle,
    ChartType,
    commonTableConstants,
    FileExtension,
    GradientColorResult,
    IBackgroundGradientProps,
    IBorderRadiusProps,
    IBrushAttribute,
    IBrushPoint,
    IChartData,
    IDynamicInfoProps,
    IFontBackground,
    IFontGlow,
    IFontShadow,
    IFontStroke,
    IGraphColors,
    IImageAiTextExtension,
    IImageEffectProps,
    IImageFillProps,
    IImageFilterProps,
    IImageTransform,
    ILinearGradient,
    IListStyleMaterial,
    IMarkupLayerProps,
    IMcpEffectProps,
    ImgCategoryType,
    ImgModeType,
    IPostBriefItem,
    IShapeExtension,
    ITableLayerProps,
    ITextContent,
    ITextLayerProps,
    ITextMark,
    ITexture,
    IVectorBorderRadiusProps,
    IVectorEffect,
    LayerType,
    MaskType,
    MediaType,
    PureCell,
    StretchOptionType,
    TextAlignType,
    TextVerticalAlign,
    TextWritingType,
    VectorTextInfo,
    VectorType,
} from '@/version-types';

export interface IServerBaseLayerProps {
    /** 元素ID */
    id: string;
    /** 类型 */
    layerType: LayerType;
    /** 宽度 */
    width: number;
    /** 高度 */
    height: number;
    /** 左边距 */
    left: number;
    /** 上边距 */
    top: number;
    /** 旋转角度 */
    rotate: number;
    /** 翻转 */
    flipH?: boolean;
    flipV?: boolean;

    /** 是否是ai图层 */
    isAILayer?: boolean;

    /** 透明度 */
    opacity: number;

    /** 图层标题 */
    title: string;

    /** 是否锁定 */
    lock: boolean;
    /** 子图层 */
    children: IServerLayerProps[];

    /** 是否闪光 */
    shine: boolean;
    /** 图层隐藏 */
    hidden: boolean;

    /** 超出隐藏 */
    overflow?: boolean;
    /** 父元素ID */
    parentId?: string;
    /** 素材ID */
    materialIds?: {
        'background-material'?: string[];
        'filter-material'?: string[];
        'mask-material'?: string[];
        'img-material'?: string[];
        'text-material'?: string[];
        'font-family'?: string[];
        'imageEffect-material'?: string[];
        'watermark-material'?: string[];
        'advanced-text-material'?: string[];
        'photo-material'?: string[];
        'vector-material'?: string[];
        'text-transform'?: string[];
        'puzzle-template-material'?: string[];
        'puzzle-poster-material'?: string[];
        'puzzle-joint-material'?: string[];
        'text-texture-material'?: string[];
        'text-mark'?: string[];
        'mcp-effect-material'?: string[];
        'text-animation'?: string[];
        'text-list'?: string[];
    };

    /** 通过ai生成该图层的prompt字段 */
    prompt?: string;

    // 用于移动端兼容未知图层缩放的场景
    fitWidth: 0;

    // 用于移动端兼容未知图层缩放的场景
    fitHeight: 0;

    extraInfo?: { [key: string]: any };
}

export interface IServerImageLayerProps extends IServerBaseLayerProps {
    url: string;
    naturalWidth: number;
    naturalHeight: number;
    imageTransform: IImageTransform;
    stretchOption: StretchOptionType;
    maskURL: string;
    maskType: MaskType;
    cutoutMaskURL: string;
    /** 图片图层标签 */
    mode?: ImgModeType;
    /** 分类 */
    category?: ImgCategoryType;

    // 图片特效
    effect?: IImageEffectProps | null;

    // 图片滤镜
    filter?: IImageFilterProps | null;

    /** 是否展示替换按钮 */
    hint?: boolean;

    /** ai花字拓展 */
    aiTextExtension?: IImageAiTextExtension | null;

    // 蒙版羽化值0~100
    maskFeathering?: number;

    // 蒙版最小框
    minimumMask?: boolean;

    blendMode?: BlendModeType;
    borderRadius?: IBorderRadiusProps;
    mcpEffects?: IMcpEffectProps[];

    // 填充颜色
    fill?: IImageFillProps;
}

export interface IServerVectorLayerProps extends IServerBaseLayerProps {
    /** 图形宽 */
    naturalWidth: number;
    /** 图形高 */
    naturalHeight: number;
    /** 图形类型 */
    vectorType: VectorType;
    /** 复杂图形-颜色列表 */
    colors: IGraphColors[];
    /** 简单图形-填充颜色 */
    fill: string;
    /** 简单图形-填充渐变色，只支持 linearGradient */
    fillGradient: ILinearGradient | null | '';
    /** 简单图形-矩形圆角 */
    radius: number;
    /** 简单图形-描边颜色 */
    stroke: string;
    /** 简单图形-描边渐变色，只支持 linearGradient */
    strokeGradient: ILinearGradient | null | '';
    /** 简单图形-描边宽度 */
    strokeWidth: number;
    /** 简单图形-描边类型 */
    strokeDasharray: string;
    /** 简单图形-描边形状 */
    strokeLinecap: string;
    /** 简单图形-描边隐藏 */
    strokeHidden?: boolean;
    /** 简单图形-特效 */
    effect?: IVectorEffect;
    /** 简单图形-模糊 */
    blur?: number;
    /** 素材地址 */
    url: string;
    /** 变换参数，裁剪，翻转 */
    imageTransform: IImageTransform;
    /** 渲染图层方式 */
    stretchOption: StretchOptionType;
    /** 图片图层标签 */
    mode?: ImgModeType;
    /** 分类 */
    category?: ImgCategoryType;
    /** 取色器索引 */
    pickerIndex?: number | null;
    /**4个圆角 */
    borderRadius?: IVectorBorderRadiusProps;

    /**
     * 矢量图层文本信息，可选字段
     * 包含该字段则说明该矢量图层为文本可编辑
     */
    textInfo?: VectorTextInfo;
}

export interface IServerBgLayerProps extends IServerBaseLayerProps {
    url: string;
    naturalWidth: number;
    naturalHeight: number;
    imageTransform: IImageTransform;
    stretchOption: StretchOptionType;
    maskURL: string;
    maskType: MaskType;
    cutoutMaskURL: string;
    /** 图片图层标签 */
    mode?: ImgModeType;
    /** 分类 */
    category?: ImgCategoryType;

    backgroundColor: string;

    backgroundGradient?: IBackgroundGradientProps | null;
    dynamicInfo?: IDynamicInfoProps;
}

export interface IServerTextLayerProps extends IServerBaseLayerProps {
    /** 文本文字 */
    text: string;
    /** 文本颜色 */
    color: string;
    /** 文本大小 */
    fontSize: number;
    /** 文本字体族 */
    fontFamily: string;
    /** 行间距 */
    lineSpacing: number;
    /** 字间距 */
    letterSpacing: number /*  */;
    /** 粗体 */
    bold: boolean;
    /** 斜体 */
    italic: boolean;
    /** 中划线 */
    lineThrough: boolean;
    /** 下划线 */
    underline: boolean;
    /** 对齐放缩 */
    textAlign: TextAlignType;
    /** 文本方向 */
    writingMode: TextWritingType;
    /** 荧光 */
    textGlow: IFontGlow[];
    /** 阴影 */
    textShadow: IFontShadow[];
    /** 描边 */
    textStroke: IFontStroke[];
    /** 文本背景色 */
    textBackground?: IFontBackground[];
    /** 文本渐变 | 背景*/
    textGradient?: GradientColorResult | null;
    /** 列表格式 */
    listStyle?: string;
    /** 分段编辑 */
    textContents?: ITextContent[];
    /** 文字形状拓展 */
    shapeExtension?: IShapeExtension | null;
    /** 文字拓展，结果图 */
    screenshotUrl?: string | null;
    /** 文字纹理 */
    texture?: ITexture | null;
    /** 划重点 */
    textMark?: ITextMark | null;
    /**
     * 垂直排版（runtime only）
     * 目前只用于（由矢量图层）派生的文本图层
     */
    verticalAlign?: TextVerticalAlign;

    /** 花字素材的配置，当使用V4.0.0花字时，需要将花字素材信息 advancedTextMaterial 拷贝一份到这里 */
    advancedTextMaterial?: { [key: string]: any };

    /** 列表资源 */
    listStyleMaterial?: IListStyleMaterial;
}

export interface IServerWatermarkLayerProps extends IServerBaseLayerProps {
    // 用户自定义上传的水印图片地址
    url: string;

    // 是否全屏水印
    isFullScreen: boolean;

    // 横向位移
    offsetX: number;

    // 纵向位移
    offsetY: number;

    // 水印大小
    size: number;

    // 间距
    space: number;

    // 交错
    cross: number;

    // 文字内容，有url时为空
    texts: string[];

    // 文字颜色#FFFFFFFF，有url时为空
    textColor: string;

    // 字体，有url时为空
    fontFamily: string;

    /** 图片图层标签 */
    mode?: ImgModeType;
    /** 分类 */
    category?: ImgCategoryType;
    zipUrl: string;

    isColorApplyToAll?: boolean;
}

export interface IServerGroupLayerProps extends IServerBaseLayerProps {
    /** 图片图层标签 */
    mode?: ImgModeType;
    /** 分类 */
    category?: ImgCategoryType;

    isTextMaterial?: boolean;
}

export interface IServerQrCodeLayerProps extends IServerBaseLayerProps {
    /** 二维码内容 */
    text: string;
    /** 公众号 */
    official: string;
    /** 背景色 */
    fill: string;
    /** 二维码颜色 */
    stroke: string;
    /** 二维码颜色渐变，只支持 linearGradient */
    strokeGradient: ILinearGradient | null;

    /** 变换参数，裁剪，翻转 */
    imageTransform: IImageTransform;
    /** 渲染图层方式 */
    stretchOption: StretchOptionType;
}

export interface IServerTableLayerProps extends IServerBaseLayerProps {
    screenshotUrl: string;
    allStyleId: string;
    cells: (PureCell | null)[][];
    baseCells: BaseCells;
    styles: Record<string, CellStyle>;
    presetId?: string;
}

interface ITableLayerProps__15_0_0 {
    screenshotUrl?: string;
    cells: {
        width: number;
        height: number;
        top: number;
        left: number;
        style?: {
            cellStyle?: {
                verticalAlign?: 'top' | 'center' | 'bottom';
                backgroundColor?: `#${string}`;
            };
            textStyle?: Partial<
                Pick<
                    ITextLayerProps,
                    'fontSize' | 'textAlign' | 'color' | 'fontFamily' | 'bold' | 'italic' | 'lineThrough' | 'underline'
                > & {
                    disableAutoWrap?: boolean;
                }
            >;
        };
        children: [
            {
                type: 'text';
                data: { text: string };
            },
        ];
    }[];
    baseCells: {
        horizontal: number[];
        vertical: number[];
    };
    borders: {
        style: 'line-0' | 'line-1' | 'line-2' | 'line-3' | 'line-4';
        width: number;
        color: `#${string}`;
        position: {
            direction: 'horizontal' | 'vertical';
            hIndex: number;
            vIndex: number;
        };
    }[];
}

export type IServerTableLayerProps__15_0_0 = IServerBaseLayerProps & ITableLayerProps__15_0_0;

export const checkServerTableLayerPropsIsVersion15_0_0 = (layer: any): layer is IServerTableLayerProps__15_0_0 =>
    !!layer.borders;

export const checkTableLayerPropsIsVersion15_0_0 = (layer: any): layer is ITableLayerProps__15_0_0 => !!layer.borders;

export const parseTableLayerProps = (oldTableLayerProps: IServerTableLayerProps__15_0_0): IServerTableLayerProps => {
    /** 两个数值大致相等 */
    const isEqualNumber = (a: number, b: number) => Math.abs(a - b) < 0.001;

    const newProps = _.cloneDeep(oldTableLayerProps);
    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
    } = newProps;
    const lefts = [
        ...horizontalSizes.map((_, index) => horizontalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        _.sum(horizontalSizes),
    ];
    const tops = [
        ...verticalSizes.map((_, index) => verticalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        _.sum(verticalSizes),
    ];

    const border: Border = {
        color: newProps.borders[0]?.color ?? commonTableConstants.borderColor,
        style: newProps.borders[0]?.style ?? 'line-0',
        width: newProps.borders[0]?.width ? newProps.borders[0].width / 2 : commonTableConstants.borderWidth,
    };

    const allStyle: CellStyle = {
        border: {
            top: border,
            right: border,
            bottom: border,
            left: border,
        },
    };

    const styles: ITableLayerProps['styles'] = {};
    /** 返回styleId */
    const appendNewCellStyle = (cellStyle: CellStyle): string => {
        const styleList = Object.entries(styles).map(([id, style]) => ({ id, style }));
        const styleItem = styleList.find((item) => _.isEqual(item.style, cellStyle));
        if (styleItem) return styleItem.id;
        const newStyleId = `${Math.max(...Object.keys(styles).map(Number), 1) + 1}`;
        styles[newStyleId] = cellStyle;
        return newStyleId;
    };

    const newCells: PureCell[] = newProps.cells.map((oldCell) => {
        type Position = Record<
            'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight',
            {
                hIndex: number;
                vIndex: number;
            }
        >;
        const position: Position = {} as any;
        // position
        lefts.forEach((left, hIndex) => {
            tops.forEach((top, vIndex) => {
                if (isEqualNumber(oldCell.left, left) && isEqualNumber(oldCell.top, top)) {
                    position.topLeft = { hIndex, vIndex };
                } else if (isEqualNumber(oldCell.left + oldCell.width, left) && isEqualNumber(oldCell.top, top)) {
                    position.topRight = { hIndex, vIndex };
                } else if (isEqualNumber(oldCell.left, left) && isEqualNumber(oldCell.top + oldCell.height, top)) {
                    position.bottomLeft = { hIndex, vIndex };
                } else if (
                    isEqualNumber(oldCell.left + oldCell.width, left) &&
                    isEqualNumber(oldCell.top + oldCell.height, top)
                ) {
                    position.bottomRight = { hIndex, vIndex };
                }
            });
        });
        const cellStyle: CellStyle = {
            ...oldCell.style?.cellStyle,
            ...oldCell.style?.textStyle,
        };
        const newStyleId = appendNewCellStyle(cellStyle);
        const cell: PureCell = {
            hIndex: position.topLeft.hIndex,
            vIndex: position.topLeft.vIndex,
            colSpan: position.bottomRight.hIndex - position.topLeft.hIndex,
            rowSpan: position.bottomRight.vIndex - position.topLeft.vIndex,
            content: oldCell.children[0].data.text,
            styleIds: [newStyleId],
        };
        return cell;
    });

    const newCellsProp: (PureCell | null)[][] = [];
    verticalSizes.forEach((_, vIndex) => {
        const trCellList: (PureCell | null)[] = [];
        newCellsProp.push(trCellList);
        horizontalSizes.forEach((_, hIndex) => {
            const cell = newCells.find((c) => c.hIndex === hIndex && c.vIndex === vIndex) ?? null;
            trCellList.push(cell);
        });
    });

    (newProps as any).cells = newCellsProp;
    delete (newProps as any).borders;
    (newProps as any).allStyleId = '1';
    (newProps as any).styles = { '1': allStyle, ...styles };

    newProps.width =
        _.sum(newProps.baseCells.horizontal) +
        (allStyle.border?.left.width ?? 0) / 2 +
        (allStyle.border?.right.width ?? 0) / 2;
    newProps.height =
        _.sum(newProps.baseCells.vertical) +
        (allStyle.border?.top.width ?? 0) / 2 +
        (allStyle.border?.bottom.width ?? 0) / 2;

    return newProps as unknown as IServerTableLayerProps;
};
export interface IServerBrushLayerProps extends IServerBaseLayerProps {
    /** 画笔类型 */
    brushType: BrushType;
    /** 画笔素材地址 */
    url: string;

    /** 填充 */
    fills: IBrushAttribute[];
    /** 描边 */
    stroke: IBrushAttribute;

    /** 箭头笔-线始端样式 */
    startPoint: IBrushPoint;
    /** 箭头笔-线末端样式 */
    endPoint: IBrushPoint;

    /** 矩形-圆角 */
    radius: number;
    /** 变换参数，裁剪，翻转 */
    imageTransform: IImageTransform;
    /** 渲染图层方式 */
    stretchOption: StretchOptionType;

    /** 图形宽 */
    naturalWidth: number;
    /** 图形高 */
    naturalHeight: number;

    /** 结果图 */
    screenshotUrl?: string | null;
}

export interface IServerMainLayerProps extends IServerBaseLayerProps {
    url: string;
    naturalWidth: number;
    naturalHeight: number;
    imageTransform: IImageTransform;
    stretchOption: StretchOptionType;
    maskURL: string;
    maskType: MaskType;
    cutoutMaskURL: string;
    /** 图片图层标签 */
    mode?: ImgModeType;
    /** 分类 */
    category?: ImgCategoryType;

    // 图片特效
    effect?: IImageEffectProps | null;

    // 图片滤镜
    filter?: IImageFilterProps | null;

    // 客户端显示提示按钮
    hint?: boolean;

    // 主图透明
    isTransparent?: boolean;
}

export interface IMagnifierLayerProps extends IServerBaseLayerProps {
    url: string;

    size: number;

    hFlip: boolean;

    strokeColor: string;

    strokeSize: number;

    enableOffset: number;

    x: number;

    y: number;

    scale: number;

    enableStroke: boolean;
}

export interface IServerPuzzleLayerProps extends IServerBaseLayerProps {
    puzzleMode: string;

    enableEdge: boolean;

    edgeOuterWidth: number;

    edgeInnerWidth: number;

    edgeCornerRadius: number;

    containerLayers: {
        width: number;
        height: number;
        left: number;
        top: number;
        rotate: number;
        stickerLayer: IServerImageLayerProps;
    }[];

    backgroundLayer: {
        backgroundColor: string;
        backgroundGradient: {
            colors: { color: string; value: number }[];
            angle: number;
            type?: number;
            point?: { x: number; y: number };
        } | null;
    };
    intelligentMode: string;
    subtitleHeightRatio: number;
    frameScale: number;
    enableSeamless: boolean;
    seamlessValue: number;
}
export interface IServerMediaLayerProps extends IServerBaseLayerProps {
    url: string;
    /** 变换参数，裁剪，翻转 */
    imageTransform: IImageTransform;
    /** 媒体类型 */
    mediaType: MediaType;
    /** 媒体宽度 */
    naturalWidth: number;
    /** 媒体高度 */
    naturalHeight: number;
    autoplay: boolean;
    loop: boolean;
    controls: boolean;
    borderRadius?: IBorderRadiusProps | null;
    muted?: boolean;
    maskURL?: string;
    poster?: string;
    duration?: number;
}

/** 纯文本图层（无限画布 Agent Teams 等） */
export interface IServerPlainTextLayerProps extends IServerBaseLayerProps {
    originText: string;
    currentText?: string;
    fontSize: number;
    fontFamily: string;
    lineHeight: number;
}

/** Markdown 文本图层 */
export interface IServerMarkdownLayerProps extends IServerBaseLayerProps {
    originText: string;
    currentText?: string;
    fontSize: number;
    fontFamily: string;
    lineHeight: number;
}

/** 文件图层 */
export interface IServerFileLayerProps extends IServerBaseLayerProps {
    size: number;
    url: string;
    extension: FileExtension;
    poster?: string;
}

/** 音频图层 */
export interface IServerAudioLayerProps extends IServerBaseLayerProps {
    size: number;
    url: string;
    duration: number;
    extension: string;
}

/** Post Brief 图层 */
export interface IServerPostBriefLayerProps extends IServerBaseLayerProps {
    items: IPostBriefItem[];
}

/** Post 帖子图层 */
export interface IServerPostLayerProps extends IServerBaseLayerProps {
    date: string;
    text: string;
    imageUrl: string;
    naturalWidth: number;
    naturalHeight: number;
}

export interface IServerChartLayerProps extends IServerBaseLayerProps {
    screenshotUrl?: string | null;

    type: ChartType;
    /**
     * 第一行是系列, 第一列是项目, 第一行第一列没有意义, 统一给null值. 用户修改值时如果没传数据, 也是null(所在位置的含义为值时当做 0 , 所在位置为文字时当做 '').
     * 如:
     * ```typescript
     * [
     *  [null, '销量', '价格'],
     *  ['衬衫', 5, 100],
     *  ['羊毛衫', 20, 200],
     *  ['雪纺衫', 36, 300],
     *  ['裤子', 10, 400],
     *  ['高跟鞋', 10, 500],
     *  ['袜子', 20, 600],
     * ]
     * ```
     * 数据被清空时, chartData的值是[], 这种场景也需要兼容
     */
    chartData: IChartData;
    /** 颜色数组, 长度必须与图表的系列数一致 */
    colors: string[];
    grid: {
        /** 有些图表类型不支持展示网格, 比如饼图, 就直接传false */
        show: boolean;
    };
    legend: {
        show: boolean;
        position: 'top' | 'bottom' | 'left' | 'right';
        textStyle: ChartTextStyle;
    };
    /** 类别坐标轴(对应chartData中的项目). 饼图时, 该值不生效. 但必须有值, 用于图表类型切换后的展示 */
    categoryAxisLabel: {
        show: boolean;
        textStyle: ChartTextStyle;
    };
    /** 值坐标轴, 同类别坐标轴 */
    valueAxisLabel: {
        show: boolean;
        textStyle: ChartTextStyle;
    };
    series: {
        itemStyle?: ChartSeriesItemStyle;
        label: {
            show: boolean;
            textStyle: ChartTextStyle;
        };
    };
}

export interface IPuzzleContainerLayerProps extends IServerBaseLayerProps {
    stickerLayer: { [key: string]: any }[];
}

export interface IServerMarkupLayerProps extends IServerBaseLayerProps, IMarkupLayerProps {}

export type IServerLayerProps =
    | IServerImageLayerProps
    | IServerTextLayerProps
    | IServerBgLayerProps
    | IServerGroupLayerProps
    | IServerWatermarkLayerProps
    | IServerVectorLayerProps
    | IServerQrCodeLayerProps
    | IServerTableLayerProps
    | IServerTableLayerProps__15_0_0
    | IServerBrushLayerProps
    | IServerMainLayerProps
    | IMagnifierLayerProps
    | IServerPuzzleLayerProps
    | IPuzzleContainerLayerProps
    | IServerMarkupLayerProps
    | IServerMediaLayerProps
    | IServerPlainTextLayerProps
    | IServerMarkdownLayerProps
    | IServerFileLayerProps
    | IServerAudioLayerProps
    | IServerPostBriefLayerProps
    | IServerPostLayerProps;
